import * as companyManagementService from "../../services/company";
import * as settings from "../../services/settings";

import { Button, Col, DatePicker, Form, Image, Input, Popconfirm, Row, Space, Table, Upload, notification } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

import { FormOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { fileTypeImage } from "../../constant/fileType";
import permissions from "../../../configs/permission";
import { useAccessToken } from "../../contexts/AccessTokenContext";
import { usePermission } from "../../hooks/usePermission";
import { resolveImageUrl } from "../../utils/imageUrl";

export const CreateCompanyBanner = ({ t, detailData }) => {
  const [api, contextHolder] = notification.useNotification();
  const { hasPermission, isCompanyFullAccess } = usePermission();
  const can = (p) => isCompanyFullAccess || hasPermission(p);
  const { accessToken } = useAccessToken();
  const [editingKey, setEditingKey] = useState(null);
  const [tableData, setTableData] = useState(defaultTableData);
  const [isSaving, setIsSaving] = useState(false);

  const [searchForm] = Form.useForm();
  const [bannerForm] = Form.useForm();
  /** เก็บ File ที่เลือกเอง — Form useWatch มักไม่อัปเดตกับ File และ File ไม่มี uid แบบ rc-upload */
  const [localBannerFile, setLocalBannerFile] = useState(null);

  const start_date = Form.useWatch("dateFrom", searchForm);
  const end_date = Form.useWatch("dateTo", searchForm);

  const localPreviewUrl = useMemo(() => {
    if (!localBannerFile || !(localBannerFile instanceof File)) return null;
    return URL.createObjectURL(localBannerFile);
  }, [localBannerFile]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const getBanner = useCallback(async () => {
    try {
      setTableData((prev) => ({ ...prev, loading: true }));
      const { start_date, end_date } = searchForm.getFieldsValue();
      const params = {
        company_id: detailData?.id,
        page: tableData.pagination.current,
        limit: tableData.pagination.pageSize,
        start_date: start_date ? dayjs(start_date).format("YYYY-MM-DD") : null,
        end_date: end_date ? dayjs(end_date).format("YYYY-MM-DD") : null,
      };
      const data = await companyManagementService.getCompanyBanner(accessToken, params);
      const datasource = data.queryCompanyBanner.map((item) => ({
        ...item,
        key: item.id,
        image: {
          image: item.image,
          name: item.image ? item.image.replace(/^uploads\//, "") : "",
          url: resolveImageUrl(item.image),
        },
      }));
      setTableData((prev) => ({ ...prev, loading: false, datasource: datasource, total: data.totalCount }));
    } catch (err) {
      console.error("[CreateCompanyBanner] getBanner: ", err);
      setTableData((prev) => ({ ...prev, loading: false }));
      const { message } = settings.getErrorMessage(err);
      api.error({ message: "ERROR.", description: message });
    }
  }, [detailData, accessToken, tableData.pagination, searchForm, api]);

  useEffect(() => {
    getBanner();
  }, [getBanner]);

  const onRefresh = () => {
    searchForm.resetFields();
    bannerForm.resetFields();
    setLocalBannerFile(null);
    setIsSaving(false);
    setEditingKey(null);
    setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
  };

  const onSave = async (rec) => {
    try {
      await bannerForm.validateFields();
      const values = bannerForm.getFieldsValue();

      const img = localBannerFile ?? values.image;
      const hasNewImageFile = Boolean(img && (img.uid || (typeof File !== "undefined" && img instanceof File)));
      const hasExistingServerImage = Boolean(rec?.image && (rec.image.image || rec.image.url) && rec.key !== "add");

      if (rec.key === "add" && !hasNewImageFile) {
        api.warning({
          message: "แจ้งเตือน",
          description: "กรุณาอัปโหลดรูปภาพ",
        });
        return;
      }
      if (rec.key !== "add" && !hasNewImageFile && !hasExistingServerImage) {
        api.warning({
          message: "แจ้งเตือน",
          description: "กรุณาอัปโหลดรูปภาพ",
        });
        return;
      }

      const formData = new FormData();
      formData.append("company_id", detailData.id);
      formData.append("links", values.link);

      if (hasNewImageFile) {
        formData.append("file", img);
      }
      if (editingKey !== "add") {
        formData.append("old_images", rec.id);
      } else {
        formData.append("old_images", "");
      }

      setIsSaving(true);
      await companyManagementService.createCompanyBanner(accessToken, formData);
      setIsSaving(false);
      api.success({
        message: "Success",
        description: editingKey === "add" ? "Create banner success" : "Update banner success",
      });

      onRefresh();
    } catch (err) {
      console.error("[CreateCompanyBanner] onSave: ", err);
      setIsSaving(false);
      const { message } = settings.getErrorMessage(err);
      api.error({ message: "ERROR.", description: message });
    }
  };

  const onCreate = () => {
    const newData = { key: "add", link: undefined };
    const newDataSource = [...tableData.datasource];
    newDataSource.unshift(newData);
    setLocalBannerFile(null);
    bannerForm.setFieldsValue(newData);
    setTableData((prev) => ({ ...prev, datasource: newDataSource }));
    setEditingKey("add");
  };

  const onCancel = () => {
    if (editingKey === "add") {
      const datasource = [...tableData.datasource];
      datasource.shift();
      setTableData((prev) => ({ ...prev, datasource }));
    }
    setLocalBannerFile(null);
    setEditingKey(null);
    bannerForm.resetFields();
  };

  const onEdit = (rec) => {
    setLocalBannerFile(null);
    setEditingKey(rec.key);
    bannerForm.setFieldsValue({ ...rec });
  };

  const onUpload = (file) => {
    if (!fileTypeImage.includes(file.type)) {
      api.error({ message: "Invalid file type", description: "Please upload file as format .png, .jpg or .jpeg." });
      return Upload.LIST_IGNORE;
    }
    setLocalBannerFile(file);
    bannerForm.setFieldsValue({ image: file });
    return false;
  };

  const onSearch = async () => {
    setTableData((prev) => ({ ...prev, pagination: { ...prev.pagination, current: 1 } }));
  };

  const columns = [
    {
      title: t("Company_Create.Create_Banner.Table.Id"),
      dataIndex: "id",
      width: 60,
    },
    {
      title: t("Company_Create.Create_Banner.Table.Image"),
      width: 550,
      render: (_, rec) => {
        if (rec.key === editingKey) {
          const src = localPreviewUrl || rec.image?.url;
          return (
            <Space className="w-full" size={24}>
              {src ? <Image src={src} className="max-h-32" alt="" /> : null}
              <div className="flex flex-col">
                <span className="mb-2">
                  {t("Company_Create.Create_Banner.Table.Currently")} :{" "}
                  {localBannerFile?.name ?? rec.image?.name ?? "-"}
                </span>
                {/* ห้ามห่อ Upload ด้วย Form.Item name="image" — Form จะ inject value/onChange ทำให้เลือกไฟล์ไม่ทำงาน */}
                <Upload beforeUpload={onUpload} showUploadList={false} accept={fileTypeImage.join(",")}>
                  <Button size="small">{t("BTN_Choose_File", { ns: "buttons" })}</Button>
                </Upload>
              </div>
            </Space>
          );
        }
        return <Image src={rec.image?.url || "default_image_url"} className="max-h-32" />;
      },
    },
    {
      title: t("Company_Create.Create_Banner.Table.Link"),
      dataIndex: "link",
      width: 300,
      render: (txt, rec) => {
        if (rec.key === editingKey) {
          return (
            <Form.Item noStyle name={"link"}>
              <Input />
            </Form.Item>
          );
        }
        return txt;
      },
    },
    {
      title: t("Company_Create.Create_Banner.Table.Create"),
      dataIndex: "created_at",
      width: 200,
      render: (_, record) => {
        const created_at = record.created_at != null ? dayjs(record.created_at).format("YYYY-MM-DD HH:mm:ss") : "-";
        return <p>{created_at}</p>;
      },
    },
    {
      title: t("Company_Create.Create_Banner.Table.Action"),
      width: 220,
      align: "center",
      render: (_, rec) => {
        if (rec.key === editingKey) {
          return (
            <Space className="m=0 p-0">
              <Button size="small" type="primary" onClick={() => onSave(rec)} loading={isSaving}>
                {t("BTN_Save", { ns: "buttons" })}
              </Button>
              {rec.key !== "add" && (
                <DeleteBannerBtn
                  record={rec}
                  detailData={detailData}
                  accessToken={accessToken}
                  onRefresh={onRefresh}
                  notiApi={api}
                  disabled={
                    isSaving ||
                    !can(permissions.CompanyBannerImage.DELETE) ||
                    !can(permissions.CompanyBannerImage.ADD) ||
                    !can(permissions.CompanyBannerImage.CHANGE)
                  }
                  t={t}
                />
              )}
              <Button size="small" onClick={onCancel}>
                {t("BTN_Cancel", { ns: "buttons" })}
              </Button>
            </Space>
          );
        }
        return (
          <Button
            icon={<FormOutlined style={{ fontSize: 20 }} />}
            type="link"
            onClick={() => onEdit(rec)}
            disabled={editingKey || !can(permissions.CompanyBannerImage.CHANGE)}
          />
        );
      },
    },
  ];

  return (
    <>
      {contextHolder}
      <section>
        <div className="my-4 text-base font-bold">{t("Company_Create.Create_Banner.Search_Title")}</div>
        <Form form={searchForm}>
          <Row gutter={[24, 24]}>
            <Col span={6}>
              <Form.Item label={t("Company_Create.Create_Banner.Search_Date_From")} name={"start_date"}>
                <DatePicker
                  format={"YYYY-MM-DD"}
                  allowClear
                  disabledDate={(cur) => (end_date ? cur.isAfter(end_date) : false)}
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label={t("Company_Create.Create_Banner.Search_Date_To")} name={"end_date"}>
                <DatePicker
                  format={"YYYY-MM-DD"}
                  allowClear
                  disabledDate={(cur) => (start_date ? cur.isBefore(start_date) : false)}
                  className="w-full"
                />
              </Form.Item>
            </Col>
            <Col>
              <Button type="primary" onClick={onSearch}>
                {t("BTN_Search", { ns: "buttons" })}
              </Button>
            </Col>
          </Row>
        </Form>
      </section>
      <section>
        <div className="flex justify-between my-4">
          <span className="text-base font-bold">{t("Company_Create.Create_Banner.Table_Title")}</span>
          {can(permissions.CompanyBannerImage.ADD) && (
            <Button type="primary" onClick={onCreate} disabled={!!editingKey}>
              {t("BTN_Create", { ns: "buttons" })}
            </Button>
          )}
        </div>
        <Form form={bannerForm}>
          <Table
            rowKey={"key"}
            loading={tableData.loading}
            columns={columns}
            dataSource={tableData.datasource}
            pagination={{
              ...tableData.pagination,
              total: tableData.total,
            }}
          />
        </Form>
      </section>
    </>
  );
};

const defaultTableData = {
  loading: false,
  datasource: [],
  pagination: { current: 1, pageSize: 10 },
  total: 0,
};

const DeleteBannerBtn = ({ record, accessToken, detailData, onRefresh, notiApi, disabled, t }) => {
  const [submitting, setSubmitting] = useState(false);

  const onConfirmDelete = async () => {
    try {
      setSubmitting(true);
      const body = { id: record.id, company_id: detailData?.id };
      await companyManagementService.deleteCompanyBanner(accessToken, body);
      notiApi.success({
        message: "Success",
        description: "Delete banner success",
      });
      onRefresh();
    } catch (err) {
      console.error("[DeleteBannerBtn] onConfirmDelete: ", err);
      // ข้อความ error แสดงจาก handlerResponseErr ใน deleteApi แล้ว
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popconfirm
      title={t("Company_Create.Create_Banner.Table.Question_Confirm_Delete")}
      okText={t("BTN_OK", { ns: "buttons" })}
      cancelText={t("BTN_Cancel", { ns: "buttons" })}
      okButtonProps={{ loading: submitting }}
      disabled={disabled}
      onConfirm={onConfirmDelete}
      getPopupContainer={() => document.body}
    >
      <Button size="small" danger disabled={disabled}>
        {t("BTN_Delete", { ns: "buttons" })}
      </Button>
    </Popconfirm>
  );
};
